import React, { MouseEvent, memo, useState } from "react";
import {
  Box,
  Button,
  Popper,
  Theme,
  Divider,
  ClickAwayListener,
  TextField,
  FormControlLabel,
  Grid,
  Grow,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import TuneIcon from "@mui/icons-material/Tune";
import { FilterBin, accounts } from "./BinForm";
import CustomizedCheckbox from "../../../common/formElements/Checkbox";

interface CardBinFilterForm {
  filter: FilterBin;
  setFilter: (value: FilterBin) => void;
  handleSubmit: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    height: 300,
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
    "& .MuiTabs-indicator": {
      backgroundColor: "#1e53a8",
    },
    "& .MuiTab-root": {
      minHeight: 30,
    },
    "& .MuiTab-wrapper": {
      alignItems: "start",
      fontSize: 14,
      textAlign: "left",
      maxWidth: 150,
    },
    "& .Mui-selected": {
      fontWeight: 600,
      background: "rgba(173, 173, 173, 0.09)",
    },
    "& .MuiTabScrollButton-vertical": {
      background: "rgba(173, 173, 173, 0.2)",
      height: "unset",
      minHeight: 25,
      transition: "opacity 0.3s ease-in-out, min-height 0.2s ease-in-out",
    },
    "& .MuiTabScrollButton-vertical.Mui-disabled": {
      minHeight: 0,
      opacity: 0,
    },
  },
  tab: {
    textTransform: "capitalize",
  },
  tabPanel: {
    width: "200px",
  },
  filterWrapper: {
    zIndex: 10,
    background: "#fff",
    marginTop: 5,
  },
  boxBorder: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 5,
    borderBottom: "none",
  },
  filterBottomSection: {
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    padding: 10,
    background: "#fafafa",
  },
  divider: {
    marginBottom: 13,
  },
  filter: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  filterBar: {
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  hide: {
    display: "none",
  },
}));

const CardBinFilter = ({
  filter,
  setFilter,
  handleSubmit,
}: CardBinFilterForm) => {
  const classes = useStyles();
  const [isShowFilter, setIsShowFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpenFilter = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setIsShowFilter(!isShowFilter);
  };

  const handleClickAway = () => {
    setIsShowFilter(false);
  };

  const handleFromChange = (field: string, value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  const handleResetFilter = () => {
    setFilter({
      from: "",
      to: "",
      length: "",
      accounts: [],
      keys: [],
      floorLimit: [],
    });
  };

  const handleSubmitFiter = () => {
    handleSubmit();
    setIsShowFilter(false);
  };

  return (
    <>
      <Box className={classes.filter}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          endIcon={<TuneIcon />}
          onClick={handleOpenFilter}
        >
          Filter Bin
        </Button>
      </Box>

      <Popper
        open={isShowFilter}
        anchorEl={anchorEl}
        placement="bottom-end"
        transition
        className={classes.filterWrapper}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Box>
              <ClickAwayListener onClickAway={handleClickAway}>
                <Box boxShadow={1} className={classes.boxBorder}>
                  <Box maxWidth={400} padding={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="From"
                          value={filter.from ?? ""}
                          onChange={(e) =>
                            handleFromChange("from", e.target.value)
                          }
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="To"
                          value={filter.to ?? ""}
                          onChange={(e) =>
                            handleFromChange("to", e.target.value)
                          }
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Length"
                          value={filter.length ?? ""}
                          onChange={(e) =>
                            handleFromChange("length", e.target.value)
                          }
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />

                        {accounts.map((account) => (
                          <FormControlLabel
                            key={account}
                            control={
                              <CustomizedCheckbox
                                key={filter.accounts.length}
                                checked={filter.accounts.includes(account)}
                                onChange={(e) =>
                                  setFilter({
                                    ...filter,
                                    accounts: e.target.checked
                                      ? [...filter.accounts, account]
                                      : filter.accounts.filter(
                                          (a) => a !== account
                                        ),
                                  })
                                }
                                name={account}
                              />
                            }
                            label={account}
                          />
                        ))}
                      </Grid>
                    </Grid>
                  </Box>

                  <Box className={classes.filterBottomSection}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      type="reset"
                      onClick={handleResetFilter}
                    >
                      Clear
                    </Button>

                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleSubmitFiter}
                    >
                      Search
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Box>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default memo(CardBinFilter);
